import {
  type ActivityDto,
  type CheckinDto,
  firstName,
  type GroupDto,
  type RollCallDto,
  type StudentDto,
  type TodayDto,
} from '@hudhuria/shared';
import { type MutationObserverOptions, type QueryClient, useMutation } from '@tanstack/react-query';
import { ApiError, api, ok } from '~/lib/api';
import { toast } from '~/lib/toast';
import type {
  ActivityVars,
  CheckinVars,
  GroupVars,
  PhotoVars,
  RegisterVars,
  RollCallVars,
  SettleVars,
} from './pending';
import { qk } from './queries';

/**
 * Mutation defaults are registered on the QueryClient (not in components) so that writes
 * restored from IndexedDB after an app restart still know how to sync themselves.
 * All writes share one scope, so they replay strictly in order (register → check in → photo).
 */
const SCOPE = { id: 'sync' };

/** Network failures retry forever with backoff (the write stays queued); real errors fail fast. */
const retry = (count: number, err: Error) => {
  if (!(err instanceof ApiError)) return count < 3;
  // offline, or signed out mid-sync: keep the write queued until it can go through
  if (err.status === 0 || err.status === 401) return true;
  if (err.status >= 500) return count < 5;
  return false;
};
const retryDelay = (count: number) => Math.min(30_000, 1000 * 2 ** count);

const failToast = (err: Error) => toast(err.message || 'Could not save — try again');

function upsertById<T extends { id: string }>(list: T[] | undefined, item: T): T[] | undefined {
  if (!list) return list;
  return list.some((x) => x.id === item.id)
    ? list.map((x) => (x.id === item.id ? item : x))
    : [item, ...list];
}

export function registerMutationDefaults(qc: QueryClient) {
  /** Shared queue behaviour for every write; each key adds its request + cache updates. */
  const define = <TData, TVars>(
    key: string,
    opts: Omit<MutationObserverOptions<TData, Error, TVars>, 'mutationKey'>,
  ) =>
    qc.setMutationDefaults<TData, Error, TVars>([key], {
      scope: SCOPE,
      retry,
      retryDelay,
      onError: failToast,
      ...opts,
    });

  define<{ checkin: CheckinDto; duplicate: boolean }, CheckinVars>('checkin', {
    mutationFn: (v: CheckinVars) =>
      ok<{ checkin: CheckinDto; duplicate: boolean }>(api.checkins.$post({ json: v.input })),
    onError: (err: Error) => {
      failToast(err);
      // an admin deleted this student while the check-in waited: drop them from this device too
      if (err instanceof ApiError && err.status === 410) void qc.invalidateQueries({ queryKey: qk.students });
    },
    onSuccess: (res: { checkin: CheckinDto; duplicate: boolean }, v: CheckinVars) => {
      qc.setQueryData<TodayDto>(qk.today(v.input.centerId, res.checkin.localDate), (old) =>
        old ? { ...old, checkins: upsertById(old.checkins, res.checkin)! } : old,
      );
      if (res.duplicate) toast(`${firstName(v.local.studentName)} was already checked in`);
    },
    onSettled: (_d: unknown, _e: unknown, v: CheckinVars) => {
      void qc.invalidateQueries({ queryKey: ['today', v.input.centerId] });
      void qc.invalidateQueries({ queryKey: qk.student(v.input.studentId) });
      if (v.local.feeMinor > 0) void qc.invalidateQueries({ queryKey: qk.students });
    },
  });

  define<{ rollCall: RollCallDto; checkins: CheckinDto[] }, RollCallVars>('rollcall', {
    mutationFn: (v: RollCallVars) =>
      ok<{ rollCall: RollCallDto; checkins: CheckinDto[] }>(api.rollcalls.$post({ json: v.input })),
    onSettled: (_d: unknown, _e: unknown, v: RollCallVars) => {
      void qc.invalidateQueries({ queryKey: ['today', v.input.centerId] });
    },
  });

  define<StudentDto, RegisterVars>('register', {
    mutationFn: (v: RegisterVars) => ok<StudentDto>(api.students.$post({ json: v.input })),
    onSuccess: (s: StudentDto, v: RegisterVars) => {
      qc.setQueryData<StudentDto[]>(qk.students, (old) => upsertById(old, s));
      if (s.ref !== v.local.provisionalRef) toast(`${firstName(s.name)} synced as ${s.ref}`);
    },
    onSettled: (_d: unknown, _e: unknown, v: RegisterVars) => {
      void qc.invalidateQueries({ queryKey: qk.students });
      void qc.invalidateQueries({ queryKey: ['today', v.input.centerId] });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });

  define<StudentDto, PhotoVars>('photo', {
    mutationFn: (v: PhotoVars) =>
      ok<StudentDto>(
        api.students[':id'].photo.$post({ param: { id: v.studentId }, json: { dataUrl: v.dataUrl } }),
      ),
    onSuccess: (s: StudentDto) => {
      qc.setQueryData<StudentDto[]>(qk.students, (old) => upsertById(old, s));
      void qc.invalidateQueries({ queryKey: qk.student(s.id) });
    },
  });

  define<ActivityDto, ActivityVars>('activity', {
    mutationFn: (v: ActivityVars) => ok<ActivityDto>(api.activities.$post({ json: v.input })),
    onSuccess: (a: ActivityDto) => {
      qc.setQueryData<ActivityDto[]>(qk.activities(a.centerId), (old) =>
        old ? [...old.filter((x) => x.id !== a.id), a] : old,
      );
    },
    onSettled: (_d: unknown, _e: unknown, v: ActivityVars) => {
      void qc.invalidateQueries({ queryKey: qk.activities(v.input.centerId) });
    },
  });

  define<GroupDto, GroupVars>('group', {
    mutationFn: (v: GroupVars) => ok<GroupDto>(api.groups.$post({ json: v.input })),
    onSuccess: (g: GroupDto) => {
      qc.setQueryData<GroupDto[]>(qk.groups(g.centerId), (old) =>
        old ? [...old.filter((x) => x.id !== g.id), g] : old,
      );
    },
    onSettled: (_d: unknown, _e: unknown, v: GroupVars) => {
      void qc.invalidateQueries({ queryKey: qk.groups(v.input.centerId) });
    },
  });

  define<{ ok: true }, SettleVars>('settle', {
    mutationFn: (v: SettleVars) =>
      ok<{ ok: true }>(
        api.payments[':id'].settle.$post({ param: { id: v.paymentId }, json: { method: v.method } }),
      ),
    onSettled: (_d: unknown, _e: unknown, v: SettleVars) => {
      void qc.invalidateQueries({ queryKey: qk.student(v.studentId) });
      void qc.invalidateQueries({ queryKey: qk.students });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

// Typed hooks. The heavy lifting (sync, cache updates) lives in the defaults above.
export const useCheckinMutation = () =>
  useMutation<unknown, Error, CheckinVars>({ mutationKey: ['checkin'] });
export const useRollCallMutation = () =>
  useMutation<unknown, Error, RollCallVars>({ mutationKey: ['rollcall'] });
export const useRegisterMutation = () =>
  useMutation<unknown, Error, RegisterVars>({ mutationKey: ['register'] });
export const usePhotoMutation = () => useMutation<unknown, Error, PhotoVars>({ mutationKey: ['photo'] });
export const useActivityMutation = () =>
  useMutation<unknown, Error, ActivityVars>({ mutationKey: ['activity'] });
export const useGroupMutation = () => useMutation<unknown, Error, GroupVars>({ mutationKey: ['group'] });
export const useSettleMutation = () => useMutation<unknown, Error, SettleVars>({ mutationKey: ['settle'] });
